import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '../../supabaseClient';
import { FaUpload, FaTimes, FaCamera } from 'react-icons/fa';
import Cropper from 'react-easy-crop';
import { getCroppedImg } from '../../utils/cropImage';

export default function Step2Photo({ wizardData, onNext, onBack, onSave, translations, isArabic, language }) {
  const t = translations || {};
  const [photoUrl, setPhotoUrl] = useState(wizardData.photo_url || null);
  const [photoFile, setPhotoFile] = useState(null);
  const [preview, setPreview] = useState(wizardData.photo_url || null);
  const [uploading, setUploading] = useState(false);
  const [errors, setErrors] = useState({});
  const fileInputRef = useRef(null);
  const fullName = wizardData.full_name || (isArabic ? 'اسمك' : 'Your Name');
  
  // Crop modal state
  const [showCropModal, setShowCropModal] = useState(false);
  const [imageToCrop, setImageToCrop] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.match(/^image\/(jpeg|jpg|png)$/)) {
      setErrors({ photo: isArabic ? 'يرجى تحميل صورة JPG أو PNG' : 'Please upload a JPG or PNG image' });
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setErrors({ photo: isArabic ? 'يجب أن يكون حجم الملف أقل من 5 ميجابايت' : 'File size must be less than 5MB' });
      return;
    }

    setErrors({});
    setPhotoFile(file);

    // Create image URL for cropping modal
    const imageUrl = URL.createObjectURL(file);
    setImageToCrop(imageUrl);
    setShowCropModal(true);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
  };

  const onCropComplete = (croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  };

  const handleCropCancel = () => {
    // Cleanup
    if (imageToCrop) {
      URL.revokeObjectURL(imageToCrop);
    }
    setShowCropModal(false);
    setImageToCrop(null);
    setPhotoFile(null);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setCroppedAreaPixels(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleCropComplete = async () => {
    if (!imageToCrop || !croppedAreaPixels) return;
    
    setUploading(true);
    setErrors({});

    try {
      // Get cropped image
      const croppedImageUrl = await getCroppedImg(imageToCrop, croppedAreaPixels);
      if (!croppedImageUrl) {
        setErrors({ photo: isArabic ? 'فشل في اقتصاص الصورة' : 'Failed to crop image' });
        setUploading(false);
        return;
      }

      // Convert blob URL to file
      const response = await fetch(croppedImageUrl);
      const blob = await response.blob();
      
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('User not authenticated');
      }

      const file = new File([blob], `profile-${user.id}-${Date.now()}.jpg`, { type: 'image/jpeg' });
      setPhotoFile(file);

      // Upload to Supabase Storage
      const filePath = `teacher-profiles/${user.id}/${file.name}`;
      const { error: uploadError } = await supabase.storage
        .from('profile-pictures')
        .upload(filePath, file, { upsert: true });

      if (uploadError) throw uploadError;

      // Get public URL
      const { data: publicUrlData } = supabase.storage
        .from('profile-pictures')
        .getPublicUrl(filePath);

      const publicUrl = publicUrlData?.publicUrl;
      if (!publicUrl) throw new Error('Failed to get public URL');

      // Cleanup crop modal
      URL.revokeObjectURL(imageToCrop);
      URL.revokeObjectURL(croppedImageUrl);
      
      setPhotoUrl(publicUrl);
      setPreview(publicUrl);
      setShowCropModal(false);
      setImageToCrop(null);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setCroppedAreaPixels(null);
    } catch (error) {
      console.error('Upload error:', error);
      setErrors({ photo: isArabic ? 'فشل تحميل الصورة. يرجى المحاولة مرة أخرى.' : 'Failed to upload photo. Please try again.' });
    } finally {
      setUploading(false);
    }
  };

  const handleRemove = () => {
    // Cleanup any object URLs
    if (imageToCrop) {
      URL.revokeObjectURL(imageToCrop);
    }
    setPhotoFile(null);
    setPhotoUrl(null);
    setPreview(null);
    setShowCropModal(false);
    setImageToCrop(null);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setCroppedAreaPixels(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (imageToCrop) {
        URL.revokeObjectURL(imageToCrop);
      }
    };
  }, [imageToCrop]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!preview) {
      setErrors({ photo: t.pleaseUploadPhoto || (isArabic ? 'يرجى تحميل صورة الملف الشخصي' : 'Please upload a profile photo') });
      return;
    }

    await onSave({ photo_url: photoUrl || preview });
    onNext({ photo_url: photoUrl || preview });
  };

  return (
    <div>
      <h2 className="step-title">{t.photo || 'Profile Photo'}</h2>
      <p className="step-subtitle">
        {t.photoSubtitle || 'Upload a clear photo of your face. It helps students trust you.'}
      </p>

      <form className="wizard-form" onSubmit={handleSubmit}>
        <div className="form-group">
          <label>{t.uploadPhoto || 'Upload Photo'} *</label>
          
          {/* Upload Box */}
          <div 
            className={`upload-box ${preview ? 'has-file' : ''}`}
            onClick={() => !uploading && !preview && fileInputRef.current?.click()}
            style={{ cursor: uploading || preview ? 'default' : 'pointer', opacity: uploading ? 0.6 : 1 }}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/jpg,image/png"
              onChange={handleFileSelect}
              disabled={uploading}
              style={{ position: 'absolute', opacity: 0, width: '100%', height: '100%', cursor: 'pointer' }}
            />
            
            {preview ? (
              <div 
                style={{ 
                  width: '100%', 
                  textAlign: 'center',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '20px',
                  position: 'relative',
                  zIndex: 10
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <img
                  src={preview}
                  alt="Profile preview"
                  style={{
                    width: '150px',
                    height: '150px',
                    borderRadius: '50%',
                    objectFit: 'cover',
                    border: '4px solid #ff7a1a',
                    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                    marginBottom: '16px',
                    display: 'block',
                    margin: '0 auto 16px auto'
                  }}
                />
                <div style={{ fontSize: '18px', fontWeight: '600', color: '#1f2937', marginBottom: '16px', textAlign: 'center' }}>
                  {fullName}
                </div>
                <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', flexWrap: 'wrap' }}>
                  <button
                    type="button"
                    onClick={async (e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      // Load existing photo for cropping
                      if (preview) {
                        try {
                          // If preview is a URL, fetch it and create object URL
                          if (preview.startsWith('http')) {
                            const response = await fetch(preview);
                            const blob = await response.blob();
                            const objectUrl = URL.createObjectURL(blob);
                            setImageToCrop(objectUrl);
                          } else {
                            // If it's a base64 data URL, use it directly
                            setImageToCrop(preview);
                          }
                          setShowCropModal(true);
                          setCrop({ x: 0, y: 0 });
                          setZoom(1);
                        } catch (error) {
                          console.error('Error loading image for crop:', error);
                          setErrors({ photo: isArabic ? 'فشل تحميل الصورة للتعديل' : 'Failed to load image for editing' });
                        }
                      }
                    }}
                    disabled={uploading}
                    style={{
                      padding: '10px 20px',
                      border: '2px solid #ff7a1a',
                      borderRadius: '10px',
                      background: 'white',
                      color: '#ff7a1a',
                      cursor: uploading ? 'not-allowed' : 'pointer',
                      fontSize: '14px',
                      fontWeight: 500,
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '8px',
                      transition: 'all 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      if (!uploading) {
                        e.currentTarget.style.background = '#fff4e6';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!uploading) {
                        e.currentTarget.style.background = 'white';
                      }
                    }}
                  >
                    <FaCamera /> {isArabic ? 'تعديل' : 'Adjust'}
                  </button>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      handleRemove();
                    }}
                    disabled={uploading}
                    style={{
                      padding: '10px 20px',
                      border: '2px solid #dc2626',
                      borderRadius: '10px',
                      background: 'white',
                      color: '#dc2626',
                      cursor: uploading ? 'not-allowed' : 'pointer',
                      fontSize: '14px',
                      fontWeight: 500,
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '8px',
                      transition: 'all 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      if (!uploading) {
                        e.currentTarget.style.background = '#fee2e2';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!uploading) {
                        e.currentTarget.style.background = 'white';
                      }
                    }}
                  >
                    <FaTimes /> {isArabic ? 'إزالة' : 'Remove'}
                  </button>
                </div>
              </div>
            ) : (
              <div style={{ position: 'relative', zIndex: 2 }}>
                <div style={{ 
                  fontSize: '56px', 
                  color: '#ff7a1a',
                  marginBottom: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <FaCamera />
                </div>
                <div>
                  <div className="upload-box-text">{t.clickToUpload || 'Click to upload photo'}</div>
                  <div className="upload-box-hint">{isArabic ? 'JPG أو PNG، حد أقصى 5 ميجابايت' : 'JPG or PNG, max 5MB'}</div>
                </div>
              </div>
            )}
          </div>

          {errors.photo && <span className="error-message">{errors.photo}</span>}
        </div>

        {/* Crop Modal */}
        {showCropModal && imageToCrop && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            zIndex: 1000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px',
            zIndex: 9999
          }}>
            <div style={{
              background: 'white',
              borderRadius: '16px',
              padding: '24px',
              maxWidth: '600px',
              width: '100%',
              maxHeight: '90vh',
              overflow: 'auto',
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
            }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '20px'
              }}>
                <h3 style={{
                  fontSize: '20px',
                  fontWeight: 600,
                  color: '#1f2937',
                  margin: 0
                }}>
                  {isArabic ? 'تعديل الصورة' : 'Adjust Photo'}
                </h3>
                <button
                  type="button"
                  onClick={handleCropCancel}
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: '24px',
                    color: '#6b7280',
                    cursor: 'pointer',
                    padding: '4px 8px',
                    borderRadius: '4px',
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = '#f3f4f6';
                    e.currentTarget.style.color = '#1f2937';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'none';
                    e.currentTarget.style.color = '#6b7280';
                  }}
                >
                  <FaTimes />
                </button>
              </div>

              {/* Cropper Container */}
              <div style={{
                position: 'relative',
                width: '100%',
                height: '400px',
                background: '#000',
                borderRadius: '12px',
                overflow: 'hidden',
                marginBottom: '20px'
              }}>
                <Cropper
                  image={imageToCrop}
                  crop={crop}
                  zoom={zoom}
                  aspect={1}
                  onCropChange={setCrop}
                  onZoomChange={setZoom}
                  onCropComplete={onCropComplete}
                  style={{
                    containerStyle: {
                      width: '100%',
                      height: '100%',
                      position: 'relative'
                    },
                    cropAreaStyle: {
                      border: '3px solid #ff7a1a',
                      boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.5)'
                    }
                  }}
                />
              </div>

              {/* Zoom Control */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                marginBottom: '24px',
                padding: '16px',
                background: '#f9fafb',
                borderRadius: '10px'
              }}>
                <label style={{
                  fontSize: '14px',
                  fontWeight: 600,
                  color: '#374151',
                  minWidth: '60px'
                }}>
                  {isArabic ? 'التكبير:' : 'Zoom:'}
                </label>
                <input
                  type="range"
                  min={1}
                  max={3}
                  step={0.1}
                  value={zoom}
                  onChange={(e) => setZoom(parseFloat(e.target.value))}
                  style={{
                    flex: 1,
                    height: '6px',
                    borderRadius: '3px',
                    background: '#e5e7eb',
                    outline: 'none',
                    cursor: 'pointer',
                    accentColor: '#ff7a1a'
                  }}
                />
                <span style={{
                  fontSize: '14px',
                  fontWeight: 600,
                  color: '#6b7280',
                  minWidth: '40px',
                  textAlign: 'right'
                }}>
                  {Math.round(zoom * 100)}%
                </span>
              </div>

              {/* Action Buttons */}
              <div style={{
                display: 'flex',
                gap: '12px',
                justifyContent: 'flex-end'
              }}>
                <button
                  type="button"
                  onClick={handleCropCancel}
                  disabled={uploading}
                  style={{
                    padding: '12px 24px',
                    background: '#f3f4f6',
                    color: '#374151',
                    border: 'none',
                    borderRadius: '10px',
                    fontWeight: 600,
                    fontSize: '15px',
                    cursor: uploading ? 'not-allowed' : 'pointer',
                    transition: 'all 0.2s',
                    opacity: uploading ? 0.5 : 1
                  }}
                  onMouseEnter={(e) => {
                    if (!uploading) {
                      e.currentTarget.style.background = '#e5e7eb';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!uploading) {
                      e.currentTarget.style.background = '#f3f4f6';
                    }
                  }}
                >
                  {isArabic ? 'إلغاء' : 'Cancel'}
                </button>
                <button
                  type="button"
                  onClick={handleCropComplete}
                  disabled={uploading || !croppedAreaPixels}
                  style={{
                    padding: '12px 24px',
                    background: uploading ? '#9ca3af' : '#ff7a1a',
                    color: 'white',
                    border: 'none',
                    borderRadius: '10px',
                    fontWeight: 600,
                    fontSize: '15px',
                    cursor: uploading || !croppedAreaPixels ? 'not-allowed' : 'pointer',
                    transition: 'all 0.2s',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                  onMouseEnter={(e) => {
                    if (!uploading && croppedAreaPixels) {
                      e.currentTarget.style.background = '#e66a00';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!uploading && croppedAreaPixels) {
                      e.currentTarget.style.background = '#ff7a1a';
                    }
                  }}
                >
                  {uploading ? (
                    <>
                      <div style={{
                        width: '16px',
                        height: '16px',
                        border: '2px solid white',
                        borderTopColor: 'transparent',
                        borderRadius: '50%',
                        animation: 'spin 0.8s linear infinite'
                      }}></div>
                      {isArabic ? 'جاري التحميل...' : 'Uploading...'}
                    </>
                  ) : (
                    <>
                      <FaUpload />
                      {isArabic ? 'حفظ الصورة' : 'Save Photo'}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="wizard-actions">
          <button type="button" className="btn btn-secondary" onClick={() => onBack({ photo_url: photoUrl || preview })} disabled={uploading}>
            {t.back || 'Back'}
          </button>
          <button type="submit" className="btn btn-primary" disabled={uploading || !preview}>
            {uploading ? (isArabic ? 'جاري التحميل...' : 'Uploading...') : (t.saveAndContinue || 'Save and continue')}
          </button>
        </div>
      </form>
    </div>
  );
}